package org.moxultimate.topwithmox.dashboard

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.apps.data.AppsResponse
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.texts.DescriptionText
import org.moxultimate.topwithmox.components.texts.TitleText
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_GAME_APP_TOGGLE
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.utils.trackDashboardCardClick
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON_DISPLAY
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_BUTTON
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_DESCRIPTION
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_LATEST_TEXT
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_PREVIEW_1_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_PREVIEW_1_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_PREVIEW_2_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_PREVIEW_2_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_PREVIEW_3_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_PREVIEW_3_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.GAMES_APPS_TITLE
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * Builds the preview list from live API data when available, falling back to static placeholders.
 * Each entry is Triple(imageUrl, title, clickUrl). Live items use the app's videoLink.
 *
 * @param apps Live apps list from the dashboard API, or null when still loading.
 * @return List of (imageUrl, title, clickUrl) triples for the 3 preview cards.
 */
private data class AppPreviewItem(
    val imageUrl: String,
    val title: String,
    val type: String,
    val clickUrl: String,
)

private fun buildAppPreviews(apps: List<AppsResponse>?): List<AppPreviewItem> =
    if (!apps.isNullOrEmpty()) {
        apps.map { app -> AppPreviewItem(app.imagePath ?: "", app.title ?: "", app.type ?: "", app.videoLink ?: "") }
    } else {
        listOf(
            AppPreviewItem(GAMES_APPS_PREVIEW_1_URL, GAMES_APPS_PREVIEW_1_TITLE, "", ""),
            AppPreviewItem(GAMES_APPS_PREVIEW_2_URL, GAMES_APPS_PREVIEW_2_TITLE, "", ""),
            AppPreviewItem(GAMES_APPS_PREVIEW_3_URL, GAMES_APPS_PREVIEW_3_TITLE, "", ""),
        )
    }

/**
 * GameAppSection
 * Dashboard section showcasing Top Games & Apps on a white background.
 * Cards are clickable — opens the item's videoLink in a new tab.
 *
 * @param apps Live list of the 3 most recent apps from the dashboard API.
 *             Null while loading — falls back to static placeholder cards.
 */
@OptIn(DelicateApi::class)
@Composable
fun GameAppSection(apps: List<AppsResponse>? = null) {
    val breakpoint = rememberBreakpoint()
    val previews = buildAppPreviews(apps)

    when (breakpoint) {
        Breakpoint.ZERO, Breakpoint.SM, Breakpoint.MD -> GameAppSectionMobile(previews)
        else -> GameAppSectionDesktop(previews)
    }
}

/**
 * GameAppSectionDesktop
 * White-background full-width stacked layout with a 3-column grid of [GameAppPreviewCard]s.
 *
 * @param previews Resolved [AppPreviewItem] list — live data or static fallback.
 */
@Composable
private fun GameAppSectionDesktop(previews: List<AppPreviewItem>) {
    val navigator = rememberAppNavigator()

    Box(
        modifier = Modifier.fillMaxWidth().padding(topBottom = 80.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.width(clamp(min = 1100.px, value = 80.vw, max = 2000.px)),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                TitleText(title = GAMES_APPS_TITLE, color = DarkTextColor)
                GradientButton(
                    text = if (DASHBOARD_GAME_APP_TOGGLE) GAMES_APPS_BUTTON else COMING_SOON,
                    buttonHeight = 50,
                    disabled = !DASHBOARD_GAME_APP_TOGGLE,
                    onClick = { navigator.goToAppsPage() },
                )
            }

            DescriptionText(
                modifier = Modifier.padding(top = 16.px),
                color = DarkTextColor,
                description = GAMES_APPS_DESCRIPTION,
            )

            if (DASHBOARD_GAME_APP_TOGGLE) {
                DescriptionText(
                    description = GAMES_APPS_LATEST_TEXT,
                    color = DarkTextColor,
                    modifier = Modifier.padding(top = 34.px, bottom = 16.px),
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier { property("flex-wrap", "wrap") },
                    horizontalArrangement = Arrangement.spacedBy(24.px),
                    verticalAlignment = Alignment.Top,
                ) {
                    previews.forEach { item ->
                        GameAppPreviewCard(
                            item = item,
                            modifier = Modifier
                                .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                                .margin(bottom = 16.px),
                        )
                    }
                }
            } else {
                Box(
                    modifier = Modifier.fillMaxWidth().padding(top = 100.px, bottom = 50.px),
                    contentAlignment = Alignment.CenterStart,
                ) {
                    TitleText(
                        title = COMING_SOON_DISPLAY,
                        color = DarkTextColor,
                        modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center),
                    )
                }
            }
        }
    }
}

/**
 * GameAppPreviewCard
 * Clickable 16:9 app thumbnail. On hover: image scales + brightens, title grows and turns
 * gradient, type subtitle appears in gradient. Opens clickUrl in a new tab on click.
 *
 * @param item     All display and interaction data for this card.
 * @param modifier Outer modifier — caller controls width and margin for grid placement.
 */
@Composable
private fun GameAppPreviewCard(
    item: AppPreviewItem,
    modifier: Modifier = Modifier,
) {
    var isHovered by remember { mutableStateOf(false) }
    val isClickable = item.clickUrl.isNotBlank()

    Column(
        modifier = modifier
            .attrsModifier {
                onMouseEnter { isHovered = true }
                onMouseLeave { isHovered = false }
                if (isClickable) onClick {
                    trackDashboardCardClick("games_apps", item.title, item.clickUrl)
                    window.open(item.clickUrl, "_blank")
                }
            }
            .styleModifier {
                if (isClickable) property("cursor", "pointer")
            },
        horizontalAlignment = Alignment.Start,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .overflow(Overflow.Hidden)
                .styleModifier { property("aspect-ratio", "16 / 9") },
        ) {
            Image(
                src = item.imageUrl,
                description = item.title,
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .styleModifier {
                        property("object-fit", "cover")
                        property("display", "block")
                        property("transition", "transform 0.3s ease, filter 0.3s ease")
                        property("transform", if (isHovered && isClickable) "scale(1.06)" else "scale(1.0)")
                        property("filter", if (isHovered && isClickable) "brightness(1.1)" else "brightness(1.0)")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        SpanText(
            text = item.title,
            modifier = Modifier
                .padding(top = 10.px)
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .fontSize(13.px)
                .fontWeight(FontWeight.SemiBold)
                .styleModifier {
                    property("display", "inline-block")
                    property("transition", "transform 0.2s ease")
                    property("transform", if (isHovered && isClickable) "scale(1.12)" else "scale(1.0)")
                    property("transform-origin", "left center")
                },
        )

        if (item.type.isNotBlank()) {
            SpanText(
                text = item.type,
                modifier = Modifier
                    .padding(top = 4.px)
                    .color(DarkTextColor)
                    .fontFamily(FontFamily)
                    .fontSize(12.px)
                    .fontWeight(FontWeight.Normal),
            )
        }
    }
}


//----- Game App Section Mobile ----------------------------------------------------------------------------------------


/**
 * GameAppSectionMobile
 * White background: stacked title/description, horizontally scrollable card row, CTA button.
 *
 * @param previews Resolved (imageUrl, title, clickUrl) triples — live data or static fallback.
 */
@OptIn(DelicateApi::class)
@Composable
private fun GameAppSectionMobile(previews: List<AppPreviewItem>) {
    val navigator = rememberAppNavigator()

    Column(
        modifier = Modifier.fillMaxWidth().padding(bottom = 40.px),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(top = 60.px, leftRight = 40.px, bottom = 20.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TitleText(title = GAMES_APPS_TITLE, color = DarkTextColor, modifier = Modifier.textAlign(TextAlign.Center))
            DescriptionText(
                modifier = Modifier.padding(top = 24.px).textAlign(TextAlign.Center),
                color = DarkTextColor,
                description = GAMES_APPS_DESCRIPTION,
            )
        }

        if (DASHBOARD_GAME_APP_TOGGLE) {
            DescriptionText(
                description = GAMES_APPS_LATEST_TEXT,
                color = DarkTextColor,
                modifier = Modifier.padding(left = 24.px, bottom = 12.px),
            )

            Row(
                modifier = WallpaperCarouselStyle.toModifier().fillMaxWidth().padding(leftRight = 16.px),
                horizontalArrangement = Arrangement.spacedBy(12.px),
                verticalAlignment = Alignment.Top,
            ) {
                previews.forEach { item ->
                    GameAppPreviewCard(
                        item = item,
                        modifier = Modifier.styleModifier {
                            property("width", "85vw")
                            property("flex-shrink", "0")
                        },
                    )
                }
            }
        }

        Box(
            modifier = Modifier.fillMaxWidth().padding(top = 32.px, bottom = 8.px),
            contentAlignment = Alignment.Center,
        ) {
            GradientButton(
                text = if (DASHBOARD_GAME_APP_TOGGLE) GAMES_APPS_BUTTON else COMING_SOON,
                disabled = !DASHBOARD_GAME_APP_TOGGLE,
                onClick = { navigator.goToAppsPage() },
            )
        }
    }
}
